import { CancelablePromise } from '@resideo/web-integration-services-api-client';
import type { ApiRequestOptions } from '@resideo/web-integration-services-api-client/dist/core/ApiRequestOptions';
import type { ApiResult } from '@resideo/web-integration-services-api-client/dist/core/ApiResult';
import { FetchHttpRequest } from '@resideo/web-integration-services-api-client/dist/core/FetchHttpRequest';
import {
  catchErrorCodes,
  getHeaders,
} from '@resideo/web-integration-services-api-client/dist/core/request';

const DOWNLOAD_FILE_URL = '/api/v1/reports/downloadableData/{requestId}/file';

const getDownloadFileUrl = (
  baseUrl: string,
  options: ApiRequestOptions,
  encodePath?: (path: string) => string
) => {
  const requestId = options.path?.requestId;
  const encodedRequestId = (encodePath ?? encodeURI)(String(requestId));
  return `${baseUrl}/api/v1/reports/downloadableData/${encodedRequestId}/file`;
};

export class BinaryAwareFetchHttpRequest extends FetchHttpRequest {
  override request<T>(options: ApiRequestOptions): CancelablePromise<T> {
    if (!(options.method === 'GET' && options.url === DOWNLOAD_FILE_URL)) {
      return super.request(options);
    }

    return new CancelablePromise<T>(async (resolve, reject, onCancel) => {
      try {
        const headers = await getHeaders(this.config, options);
        headers.set('Accept', 'application/octet-stream');
        const controller = new AbortController();

        const requestInit: RequestInit = {
          method: options.method,
          headers,
          signal: controller.signal,
        };

        if (this.config.WITH_CREDENTIALS) {
          requestInit.credentials = this.config.CREDENTIALS;
        }

        onCancel(() => controller.abort());

        const url = getDownloadFileUrl(
          this.config.BASE,
          options,
          this.config.ENCODE_PATH
        );

        const response = await fetch(url, requestInit);
        const contentType = response.headers.get('content-type')?.toLowerCase();
        let responseBody: Blob;

        if (contentType?.includes('application/json')) {
          const jsonPayload = await response
            .json()
            .catch(() => null as Record<string, unknown> | null);
          const dataPayload = jsonPayload?.data as
            | Record<string, unknown>
            | undefined;

          const downloadableUrl =
            (jsonPayload?.url as string | undefined) ||
            (dataPayload?.url as string | undefined);

          if (downloadableUrl) {
            const fileResponse = await fetch(downloadableUrl, {
              method: 'GET',
              signal: controller.signal,
            });

            if (!fileResponse.ok) {
              throw new Error(
                `Download URL request failed with status ${fileResponse.status}`
              );
            }

            responseBody = await fileResponse.blob();
          } else {
            throw new Error(
              `Download response JSON does not include a file URL for ${url}`
            );
          }
        } else {
          responseBody = await response.blob();
        }

        const result: ApiResult = {
          url,
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          body: responseBody,
        };

        catchErrorCodes(options, result);
        resolve(result.body as T);
      } catch (error) {
        reject(error);
      }
    });
  }
}
